import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/base/Seo';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da25ji5v7gurs51ef7gg';

const channels = [
  {
    icon: 'ri-mail-send-line',
    title: '官方邮箱',
    value: 'hello@mugua-manhua.com',
    note: '内容反馈、商务合作',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '在线客服',
    value: '周一至周日 9:00 - 22:00',
    note: '最快 2 小时内回复',
  },
  {
    icon: 'ri-message-3-line',
    title: '社区留言',
    value: '读者交流区',
    note: '漫画心得、追更讨论',
  },
  {
    icon: 'ri-file-copy-line',
    title: '版权合作',
    value: 'copyright@mugua-manhua.com',
    note: '授权与版权相关事宜',
  },
];

export default function ContactPage() {
  const [formError, setFormError] = useState('');
  const [formOk, setFormOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get('phone_alt') || '').toString().trim();
    if (honeypot) {
      setFormOk(true);
      setFormError('');
      return;
    }
    fd.delete('phone_alt');

    setSubmitting(true);
    setFormError('');
    setFormOk(false);
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      if (res.ok && parsed?.code === 'OK') {
        setFormOk(true);
        form.reset();
      } else {
        setFormError(
          serverMsg && !serverMsg.includes('spam') ? serverMsg : '提交失败，请稍后再试',
        );
      }
    } catch {
      setFormError('网络异常，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 麻瓜漫画网"
        description="联系麻瓜漫画网：提交留言反馈、内容合作或版权事宜。官方邮箱、在线客服与留言表单，我们会尽快回复你的每一条信息。"
        keywords="麻瓜漫画网,联系我们,联系邮箱,反馈,版权合作"
        canonical="https://uncutm.github.io/contact"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: '联系麻瓜漫画网',
          url: 'https://uncutm.github.io/contact',
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'Organization',
            name: '麻瓜漫画网',
            email: 'hello@mugua-manhua.com',
            url: 'https://uncutm.github.io/',
          },
        }}
      />

      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-16">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">联系我们</span>
        </nav>

        <div className="mt-8">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
            无论是内容反馈、追更建议、版权合作还是其他任何问题，都可以通过下面的方式联系我们，
            我们会认真对待每一条留言。
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* channels */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {channels.map((c) => (
              <div key={c.title} className="flex items-start gap-4 rounded-xl bg-background-100 p-5 ring-1 ring-background-200/50">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950">{c.title}</p>
                  <p className="mt-1 break-all text-sm text-primary-500">{c.value}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{c.note}</p>
                </div>
              </div>
            ))}
            <div className="rounded-xl bg-accent-500/10 p-5 ring-1 ring-accent-500/30">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-700">
                <i className="ri-time-line"></i> 客服时间
              </p>
              <p className="mt-2 text-sm text-foreground-600">
                工作日与节假日 9:00 - 22:00，我们通常会在 2 小时内回复你的留言。
              </p>
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-3 rounded-2xl bg-background-100 p-6 md:p-8 ring-1 ring-background-200/60">
            <h2 className="font-heading text-xl text-foreground-950">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-500">填写以下表单，我们会尽快与你联系</p>

            <form onSubmit={onSubmit} data-readdy-form id="contact-form" className="mt-6 flex flex-col gap-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-semibold text-foreground-700">
                    您的称呼
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    maxLength={30}
                    placeholder="怎么称呼您"
                    className="h-11 w-full rounded-lg bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-1 ring-background-300/50 focus:ring-2 focus:ring-primary-400/60"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-semibold text-foreground-700">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="用于回复您"
                    className="h-11 w-full rounded-lg bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-1 ring-background-300/50 focus:ring-2 focus:ring-primary-400/60"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-2 block text-sm font-semibold text-foreground-700">
                  留言主题
                </label>
                <select
                  id="topic"
                  name="topic"
                  className="h-11 w-full rounded-lg bg-background-50 px-4 text-sm text-foreground-950 outline-none ring-1 ring-background-300/50 focus:ring-2 focus:ring-primary-400/60"
                >
                  <option value="内容反馈">内容反馈</option>
                  <option value="追更建议">追更建议</option>
                  <option value="版权合作">版权合作</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-semibold text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请详细描述你的问题或建议（500 字以内）"
                  className="w-full resize-none rounded-lg bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-1 ring-background-300/50 focus:ring-2 focus:ring-primary-400/60"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-500">限 500 字以内</p>
              </div>

              {/* honeypot */}
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="phone_alt">电话（请勿填写）</label>
                <input
                  type="text"
                  id="phone_alt"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {formError && (
                <p role="alert" className="flex items-center gap-2 rounded-lg bg-primary-500/15 px-4 py-3 text-sm text-primary-500">
                  <i className="ri-error-warning-line"></i> {formError}
                </p>
              )}
              {formOk && (
                <p role="status" className="flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line"></i> 提交成功，我们会尽快回复你！
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-full bg-primary-500 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? '正在提交…' : '提交留言'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}